// ============================================================================
// @alloyhead/core — Kernel Registry
// ============================================================================
// Named compute kernel dispatch. Apps register kernels (WASM or JS) at init
// time, and the registry picks the fastest available implementation at runtime.
//
// This replaces the pattern where app code manually checks wasm availability:
//   if (bridge._wasmAvailable) bridge.runKernel("blur", ...) else jsBlur(...)
//
// With:
//   kernels.run("blur", data, w, h, { radius: 5 });
//   // → routes to WASM if available, JS fallback otherwise
//
// Design principles:
//   - Kernels are registered at init time, not discovered at runtime.
//   - No dynamic loading. All kernel code is bundled.
//   - Dual-path: each kernel can have both a WASM export name and a JS fallback.
//   - The registry is feature-gate-aware: can check FeatureRegistry before running.
//   - Kernel registration is idempotent — re-registering overwrites.
// ============================================================================
/**
 * Registry of named compute kernels. Dispatch to WASM or JS based on availability.
 */
export class KernelRegistry {
    kernels = new Map();
    wasmRunner = null;
    features = null;
    /**
     * Attach a WASM kernel runner (typically a WasmKernelBridge instance).
     * When attached, kernels with `wasmExport` will prefer the WASM path.
     */
    setWasmRunner(runner) {
        this.wasmRunner = runner;
    }
    /**
     * Attach a feature registry for feature-gated kernels.
     */
    setFeatureRegistry(features) {
        this.features = features;
    }
    /**
     * Register a kernel. Overwrites any existing kernel with the same name.
     */
    register(def) {
        if (!def.name)
            throw new Error('Kernel must have a name');
        if (!def.js && !def.wasmExport) {
            throw new Error(`Kernel "${def.name}" must have at least a js or wasmExport implementation`);
        }
        this.kernels.set(def.name, def);
    }
    /**
     * Register multiple kernels at once.
     */
    registerAll(defs) {
        for (const def of defs)
            this.register(def);
    }
    /**
     * Check if a kernel is registered.
     */
    has(name) {
        return this.kernels.has(name);
    }
    /**
     * List all registered kernel names.
     */
    list() {
        return Array.from(this.kernels.keys());
    }
    /**
     * Get a kernel definition by name (or null if not registered).
     */
    get(name) {
        return this.kernels.get(name) ?? null;
    }
    /**
     * Run a named kernel on the provided data.
     *
     * Routing order:
     * 1. Check feature gate (if set) — skip if disabled
     * 2. Try WASM path (if wasmRunner attached + kernel has wasmExport)
     * 3. Fall back to JS implementation
     * 4. Return false if no implementation available
     *
     * @returns KernelRunResult with execution details
     */
    run(name, data, width, height, params) {
        const def = this.kernels.get(name);
        if (!def) {
            return { ok: false, durationMs: 0, path: 'skipped', error: `Kernel "${name}" not registered` };
        }
        // Feature gate check
        if (def.featureGate && this.features) {
            if (!this.features.isEnabled(def.featureGate)) {
                return { ok: false, durationMs: 0, path: 'skipped', error: `Feature "${def.featureGate}" is disabled` };
            }
        }
        const t0 = performance.now();
        // Try WASM path
        if (def.wasmExport && this.wasmRunner?.available) {
            try {
                const ok = this.wasmRunner.runKernel(def.wasmExport, data, width, height, params);
                if (ok) {
                    return { ok: true, durationMs: performance.now() - t0, path: 'wasm' };
                }
                // WASM returned false — fall through to JS
            }
            catch (err) {
                // WASM threw — fall through to JS
            }
        }
        // Try JS path
        if (def.js) {
            try {
                def.js(data, width, height, params);
                return { ok: true, durationMs: performance.now() - t0, path: 'js' };
            }
            catch (err) {
                const message = err instanceof Error ? err.message : String(err);
                return { ok: false, durationMs: performance.now() - t0, path: 'js', error: message };
            }
        }
        return { ok: false, durationMs: 0, path: 'skipped', error: `Kernel "${name}" has no available implementation` };
    }
    /**
     * Run a kernel and return only the success boolean (for hot-path use).
     * Equivalent to `run(...).ok` but avoids allocating the result object.
     */
    runFast(name, data, width, height, params) {
        const def = this.kernels.get(name);
        if (!def)
            return false;
        if (def.featureGate && this.features && !this.features.isEnabled(def.featureGate))
            return false;
        if (def.wasmExport && this.wasmRunner?.available) {
            try {
                if (this.wasmRunner.runKernel(def.wasmExport, data, width, height, params))
                    return true;
            }
            catch { /* fall through */ }
        }
        if (def.js) {
            try {
                def.js(data, width, height, params);
                return true;
            }
            catch {
                return false;
            }
        }
        return false;
    }
    /**
     * Remove a kernel by name.
     */
    unregister(name) {
        return this.kernels.delete(name);
    }
    /**
     * Remove all registered kernels.
     */
    clear() {
        this.kernels.clear();
    }
}
//# sourceMappingURL=kernel-registry.js.map